import { FormEvent, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  KontaktInput,
  createKontakt,
  deactivateKontakt,
  fetchKontakte,
  fetchKontaktFunktionen,
} from "@/lib/api"
import { EmployeePicker } from "@/components/EmployeePicker"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { Button, FieldError, Input, Select } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const LEER: KontaktInput = {
  anrede: "",
  vorname: "",
  nachname: "",
  funktionId: null,
  durchwahl: "",
  employeeId: null,
}

/** Ansprechpartner-Verwaltung innerhalb der Organisation-Detailseite (Ninox `K`). */
export function KontakteSection({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [neu, setNeu] = useState<KontaktInput>(LEER)
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [nachnameFehler, setNachnameFehler] = useState<string | null>(null)
  const [deaktivierenId, setDeaktivierenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["kontakte", "organisation", organisationId],
    queryFn: () => fetchKontakte(organisationId),
  })

  const { data: funktionen } = useQuery({
    queryKey: ["kontakt-funktionen"],
    queryFn: fetchKontaktFunktionen,
  })

  const funktionName = (id: string | null) =>
    id === null ? null : funktionen?.find((f) => f.id === id)?.name ?? null

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["kontakte", "organisation", organisationId] })

  const anlegen = useMutation({
    mutationFn: (payload: KontaktInput) => createKontakt({ ...payload, organisationId }),
    onSuccess: () => {
      invalidieren()
      setNeu(LEER)
      setZeigeNeu(false)
      toast.show("Kontakt gespeichert.", "success")
    },
    onError: () => toast.show("Kontakt konnte nicht gespeichert werden.", "error"),
  })

  const deaktivieren = useMutation({
    mutationFn: (id: string) => deactivateKontakt(id),
    onSuccess: () => {
      invalidieren()
      setDeaktivierenId(null)
      toast.show("Kontakt deaktiviert.", "success")
    },
    onError: () => toast.show("Kontakt konnte nicht deaktiviert werden.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    if (!neu.nachname.trim()) {
      setNachnameFehler("Nachname ist erforderlich.")
      return
    }
    setNachnameFehler(null)
    anlegen.mutate(neu)
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Kontakte</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Kontakt hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-10 w-full" />
          <SkeletonZeile className="h-10 w-full" />
        </div>
      )}

      <ul className="space-y-2">
        {data?.map((k) => (
          <li
            key={k.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">
                {[k.anrede, k.vorname, k.nachname].filter(Boolean).join(" ") || "—"}
                {!k.aktiv && <span className="ml-2 text-xs text-slate-400">(inaktiv)</span>}
              </p>
              <p className="text-slate-500">
                {funktionName(k.funktionId) ? `${funktionName(k.funktionId)} · ` : ""}
                {k.durchwahl ? `Durchwahl ${k.durchwahl}` : ""}
                {k.employeeAnzeigename ? ` · intern: ${k.employeeAnzeigename}` : ""}
                {!funktionName(k.funktionId) && !k.durchwahl && !k.employeeAnzeigename ? "—" : ""}
              </p>
            </div>
            {k.aktiv &&
              (deaktivierenId === k.id ? (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-slate-600">Wirklich deaktivieren?</span>
                  <button
                    type="button"
                    onClick={() => deaktivieren.mutate(k.id)}
                    disabled={deaktivieren.isPending}
                    className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  >
                    Ja, deaktivieren
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeaktivierenId(null)}
                    className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  >
                    Abbrechen
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeaktivierenId(k.id)}
                  className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Deaktivieren
                </button>
              ))}
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Kontakte erfasst."
          aktion={{ label: "+ Kontakt hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="kontakt-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neuer Kontakt</SheetTitle>
            <SheetDescription>Ansprechpartner für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="kontakt-section-neu-form" onSubmit={onSubmitNeu} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Anrede</label>
              <Select
                value={neu.anrede ?? ""}
                onChange={(e) => setNeu({ ...neu, anrede: e.target.value || null })}
              >
                <option value="">—</option>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Vorname</label>
              <Input
                value={neu.vorname ?? ""}
                onChange={(e) => setNeu({ ...neu, vorname: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="kontakt-section-nachname"
                className="block text-xs font-medium text-slate-700"
              >
                Nachname *
              </label>
              <Input
                id="kontakt-section-nachname"
                required
                value={neu.nachname}
                onChange={(e) => {
                  setNeu({ ...neu, nachname: e.target.value })
                  if (nachnameFehler) setNachnameFehler(null)
                }}
                invalid={!!nachnameFehler}
                aria-describedby={nachnameFehler ? "kontakt-section-nachname-fehler" : undefined}
              />
              <FieldError id="kontakt-section-nachname-fehler">{nachnameFehler}</FieldError>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Funktion</label>
              <Select
                value={neu.funktionId ?? ""}
                onChange={(e) => setNeu({ ...neu, funktionId: e.target.value || null })}
              >
                <option value="">—</option>
                {funktionen?.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Durchwahl</label>
              <Input
                value={neu.durchwahl ?? ""}
                onChange={(e) => setNeu({ ...neu, durchwahl: e.target.value })}
              />
            </div>
            <div>
              <EmployeePicker
                label="Interner Ansprechpartner"
                value={neu.employeeId ?? null}
                onChange={(id: string | null) => setNeu((f) => ({ ...f, employeeId: id }))}
              />
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="kontakt-section-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Kontakt speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
